'use client';

import { FC, ReactNode } from 'react';

import { OptimizelyProvider } from '@optimizely/react-sdk';

import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';
import { useHybrisSegment } from '@telialt/utils/src/segment';

import { USER_GROUP_KEYS } from './constants/constants';
import { createReactSDKInstance } from './createReactSDKInstance';
import { getOptimizelyConfig } from './getOptimizelyConfig';
import { getSdkKey } from './getSdkKey';
import { useCustomerSettingsBasicQuery } from './hooks/useCustomerSettingsBasicQuery';
import { useCustomerSettingsGroupsQuery } from './hooks/useCustomerSettingsGroupsQuery';
import { useDataFileQuery } from './hooks/useDataFileQuery';

type Props = {
    children?: ReactNode;
    hasGrouping?: boolean;
    hasTargeting?: boolean;
    loader?: ReactNode;
    isServerSide?: boolean;
};

export const Optimizely: FC<Props> = ({
    children,
    hasGrouping = false,
    hasTargeting = false,
    loader,
    isServerSide = false,
}) => {
    const { customerGroupsData, isCustomerGroupsDataLoading, customerGroupsDataError } =
        useCustomerSettingsGroupsQuery(hasGrouping);
    const { customerBasicsData, isCustomerBasicsDataLoading, customerBasicsDataError } =
        useCustomerSettingsBasicQuery(hasTargeting);
    const { dataFile, isDataFileLoading, dataFileError } = useDataFileQuery(getSdkKey());
    useLogError(customerGroupsDataError || customerBasicsDataError || dataFileError, DDComponent.Optimizely);

    const segment = useHybrisSegment();

    if (
        (hasGrouping && isCustomerGroupsDataLoading) ||
        (hasTargeting && isCustomerBasicsDataLoading) ||
        isDataFileLoading
    ) {
        return loader || null;
    }

    const config = getOptimizelyConfig(dataFile || {});

    return (
        <OptimizelyProvider
            isServerSide={isServerSide}
            optimizely={createReactSDKInstance(config)}
            user={{
                id: customerBasicsData?.id || 'anonymous',
                attributes: {
                    isInPoPGroup:
                        hasGrouping &&
                        !customerGroupsDataError &&
                        customerGroupsData?.groups.includes(USER_GROUP_KEYS.POP),
                    isInSimTerminationGroup:
                        hasGrouping &&
                        !customerGroupsDataError &&
                        customerGroupsData?.groups.includes(USER_GROUP_KEYS.SIM_TERMINATION),
                    isInUOAutoOrderGroup:
                        hasGrouping &&
                        !customerGroupsDataError &&
                        customerGroupsData?.groups.includes(USER_GROUP_KEYS.UO_AUTO_ORDER_GROUP),
                    isInFixInternetAutoOrderGroup:
                        hasGrouping &&
                        !customerGroupsDataError &&
                        customerGroupsData?.groups.includes(USER_GROUP_KEYS.FIX_INTERNET_AUTO_ORDER_GROUP),
                    isInFSecureGroup:
                        hasGrouping &&
                        !customerGroupsDataError &&
                        customerGroupsData?.groups.includes(USER_GROUP_KEYS.F_SECURE),
                    isInRoamingTariffsGroup:
                        hasGrouping &&
                        !customerGroupsDataError &&
                        customerGroupsData?.groups.includes(USER_GROUP_KEYS.ROAMING_TARIFFS),
                    isInHighlightingVASGroup:
                        hasGrouping &&
                        !customerGroupsDataError &&
                        customerGroupsData?.groups.includes(USER_GROUP_KEYS.HIGHLIGHTING_VAS),
                    isInNetflixVASGroup:
                        hasGrouping &&
                        !customerGroupsDataError &&
                        customerGroupsData?.groups.includes(USER_GROUP_KEYS.NETFLIX_VAS_GROUP),
                    isInDisneyVASGroup:
                        hasGrouping &&
                        !customerGroupsDataError &&
                        customerGroupsData?.groups.includes(USER_GROUP_KEYS.DISNEY_VAS_GROUP),
                    segment,
                },
            }}
        >
            {children}
        </OptimizelyProvider>
    );
};
